import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MagnifyingGlassIcon, XIcon, ArrowDownIcon, ArrowUpIcon, ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { ScriptBlockData } from './ScriptBlock';
import { Button } from '../ui/button';
import {scriptTextFields} from '../../lib/scriptDualDialogue';

interface FindReplaceProps {
    isOpen: boolean;
    onClose: () => void;
    blocks: ScriptBlockData[];
    onNavigateToBlock: (blockId: string) => void;
    onReplaceInBlock: (blockId: string, oldText: string, newText: string, paragraphId?:string) => void;
    onReplaceAll: (oldText: string, newText: string) => void;
}

interface Match {
    blockId: string;
    blockIndex: number;
    startIndex: number;
    endIndex: number;
    context: string;
    paragraphId?:string;
    text:string;
}

export const FindReplace: React.FC<FindReplaceProps> = ({
    isOpen,
    onClose,
    blocks,
    onNavigateToBlock,
    onReplaceInBlock,
    onReplaceAll
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [replaceTerm, setReplaceTerm] = useState('');
    const [matches, setMatches] = useState<Match[]>([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [showReplace, setShowReplace] = useState(false);
    const [caseSensitive, setCaseSensitive] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Focus search input when opened
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
            searchInputRef.current.select();
        }
    }, [isOpen]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            if (e.key === 'Escape') {
                onClose();
            } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                goToNextMatch();
            } else if (e.key === 'Enter' && e.shiftKey) {
                e.preventDefault();
                goToPrevMatch();
            } else if ((e.ctrlKey || e.metaKey) && e.key === 'h') {
                e.preventDefault();
                setShowReplace(prev => !prev);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, matches, currentMatchIndex]);

    // Search for matches
    useEffect(() => {
        if (!searchTerm.trim()) {
            setMatches([]);
            setCurrentMatchIndex(0);
            return;
        }

        const newMatches: Match[] = [];
        const searchRegex = new RegExp(
            searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
            caseSensitive ? 'g' : 'gi'
        );

        blocks.forEach((block,blockIndex) => {
            for(const field of scriptTextFields(block)) {
                searchRegex.lastIndex=0;
                let match;
                while((match=searchRegex.exec(field.text))!==null) newMatches.push({blockId:block.id,blockIndex,
                    startIndex:match.index,endIndex:match.index+match[0].length,paragraphId:field.paragraphId,text:match[0],
                    context:field.text.substring(Math.max(0,match.index-20),match.index+match[0].length+20)});
            }
        });

        setMatches(newMatches);
        setCurrentMatchIndex(0);

        // Navigate to first match
        if (newMatches.length > 0) {
            onNavigateToBlock(newMatches[0].blockId);
        }
    }, [searchTerm, blocks, caseSensitive]);

    const goToNextMatch = useCallback(() => {
        if (matches.length === 0) return;
        const nextIndex = (currentMatchIndex + 1) % matches.length;
        setCurrentMatchIndex(nextIndex);
        onNavigateToBlock(matches[nextIndex].blockId);
    }, [matches, currentMatchIndex, onNavigateToBlock]);

    const goToPrevMatch = useCallback(() => {
        if (matches.length === 0) return;
        const prevIndex = currentMatchIndex === 0 ? matches.length - 1 : currentMatchIndex - 1;
        setCurrentMatchIndex(prevIndex);
        onNavigateToBlock(matches[prevIndex].blockId);
    }, [matches, currentMatchIndex, onNavigateToBlock]);

    const handleReplace = () => {
        if (matches.length === 0 || !replaceTerm) return;
        const match = matches[currentMatchIndex];
        onReplaceInBlock(match.blockId, match.text, replaceTerm,match.paragraphId);
    };

    const handleReplaceAll = () => {
        if (!searchTerm.trim() || !replaceTerm) return;
        onReplaceAll(searchTerm, replaceTerm);
        setSearchTerm('');
        setMatches([]);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed top-16 z-[100] w-80 bg-zinc-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" style={{ right: 'calc(var(--sidebar-right, 0px) + 24px)' }}>
            {/* Search Row */}
            <div className="p-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg focus-within:border-indigo-500/50">
                        <MagnifyingGlassIcon className="w-4 h-4 text-zinc-500" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Find in script..."
                            className="flex-1 bg-transparent border-none outline-none text-sm text-zinc-200 placeholder:text-zinc-600"
                        />
                        {searchTerm && (
                            <span className="text-[10px] text-zinc-500">
                                {matches.length > 0 ? `${currentMatchIndex + 1}/${matches.length}` : '0'}
                            </span>
                        )}
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                    >
                        <XIcon className="w-4 h-4" />
                    </Button>
                </div>

                {/* Navigation & Options */}
                <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={goToPrevMatch}
                            disabled={matches.length === 0}
                            className="h-7 w-7 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                            title="Previous (Shift+Enter)"
                        >
                            <ArrowUpIcon className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={goToNextMatch}
                            disabled={matches.length === 0}
                            className="h-7 w-7 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                            title="Next (Enter)"
                        >
                            <ArrowDownIcon className="w-3.5 h-3.5" />
                        </Button>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCaseSensitive(!caseSensitive)}
                            className={`px-2 py-1 text-[10px] rounded transition-colors ${
                                caseSensitive
                                    ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                                    : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
                            }`}
                            title="Case Sensitive"
                        >
                            Aa
                        </button>
                        <button
                            onClick={() => setShowReplace(!showReplace)}
                            className={`px-2 py-1 text-[10px] rounded transition-colors ${
                                showReplace
                                    ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                                    : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
                            }`}
                            title="Show Replace (Ctrl+H)"
                        >
                            Replace
                        </button>
                    </div>
                </div>
            </div>

            {/* Replace Row */}
            {showReplace && (
                <div className="p-3 border-b border-white/5 bg-white/[0.02]">
                    <div className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg focus-within:border-indigo-500/50">
                        <ArrowsClockwiseIcon className="w-4 h-4 text-zinc-500" />
                        <input
                            type="text"
                            value={replaceTerm}
                            onChange={(e) => setReplaceTerm(e.target.value)}
                            placeholder="Replace with..."
                            className="flex-1 bg-transparent border-none outline-none text-sm text-zinc-200 placeholder:text-zinc-600"
                        />
                    </div>

                    <div className="flex items-center gap-2 mt-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleReplace}
                            disabled={matches.length === 0 || !replaceTerm}
                            className="h-7 px-3 text-xs text-zinc-300 hover:text-white disabled:opacity-30"
                        >
                            Replace
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleReplaceAll}
                            disabled={matches.length === 0 || !replaceTerm}
                            className="h-7 px-3 text-xs text-zinc-300 hover:text-white disabled:opacity-30"
                        >
                            Replace All ({matches.length})
                        </Button>
                    </div>
                </div>
            )}

            {/* Results Preview */}
            {matches.length > 0 && (
                <div className="max-h-48 overflow-y-auto">
                    {matches.slice(0, 10).map((match, index) => {
                        const block = blocks[match.blockIndex];
                        return (
                            <button
                                key={`${match.blockId}-${match.startIndex}`}
                                onClick={() => {
                                    setCurrentMatchIndex(index);
                                    onNavigateToBlock(match.blockId);
                                }}
                                className={`w-full p-2 text-left text-xs border-b border-white/5 transition-colors ${
                                    index === currentMatchIndex
                                        ? 'bg-indigo-500/20'
                                        : 'hover:bg-white/5'
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[9px] uppercase text-zinc-500">{block?.type}</span>
                                    <span className="text-zinc-600">•</span>
                                    <span className="text-[9px] text-zinc-500">Block {match.blockIndex + 1}</span>
                                </div>
                                <div className="text-zinc-400 truncate">
                                    ...{match.context}...
                                </div>
                            </button>
                        );
                    })}
                    {matches.length > 10 && (
                        <div className="p-2 text-center text-[10px] text-zinc-500">
                            +{matches.length - 10} more matches
                        </div>
                    )}
                </div>
            )}

            {/* Footer */}
            <div className="p-2 bg-white/[0.02] border-t border-white/5">
                <div className="flex items-center justify-between text-[9px] text-zinc-600">
                    <span>↑↓ Navigate</span>
                    <span>Enter to go to next</span>
                    <span>Esc to close</span>
                </div>
            </div>
        </div>
    );
};
